import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { auth } from "../lib/api";
import { plansPublicApi } from "../lib/plansApi";
import PlanCard from "../components/PlanCard";
import PublicFooter from "../components/PublicFooter";
import TermsViewModal from "../components/TermsViewModal";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

const BUSINESS_TYPES = [
    { id: "barbearia", label: "Barbearia", icon: "fa-mustache" },
    { id: "salao", label: "Salão de Beleza", icon: "fa-scissors" },
    { id: "estetica", label: "Studio de Estética", icon: "fa-spa" },
    { id: "outro", label: "Outro", icon: "fa-store" },
];

const PLANS_FALLBACK = [
    { id: "royal", slug: "royal", name: "Royal", price_monthly: 97, trial_days: 14, color: "#c9983a", description: "Para pequenos salões",
      limits: { clients: -1, professionals: 5, storage_gb: 5 },
      features: { agenda: true, pdv: true, whatsapp: true, financial: true, waitlist: true, public_booking: true } },
];

const maskPhone = (v) => {
    const d = String(v || "").replace(/\D/g, "").slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 6) return `(${d.slice(0,2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
    return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
};
const maskDoc = (v) => {
    const d = String(v || "").replace(/\D/g, "").slice(0, 14);
    if (d.length <= 11) {
        // CPF: 000.000.000-00
        return d
            .replace(/^(\d{3})(\d)/, "$1.$2")
            .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
            .replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
    }
    // CNPJ: 00.000.000/0000-00
    return d
        .replace(/^(\d{2})(\d)/, "$1.$2")
        .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
        .replace(/\.(\d{3})(\d)/, ".$1/$2")
        .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};
const detectDocType = (v) => (String(v || "").replace(/\D/g, "").length > 11 ? "cnpj" : "cpf");

const Stepper = ({ current }) => (
    <div className="flex items-center gap-2 mb-8 justify-center">
        {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-2">
                <div className={`h-8 w-8 rounded-full border flex items-center justify-center text-xs font-bold transition-all ${
                    current >= n
                        ? "bg-amber-500 border-amber-400 text-slate-950 shadow-[0_0_14px_-3px_rgba(245,158,11,0.7)]"
                        : "bg-slate-900 border-white/10 text-slate-500"
                }`}>
                    {current > n ? <i className="fa-solid fa-check text-[10px]"></i> : n}
                </div>
                {n < 3 && <div className={`h-px w-10 ${current > n ? "bg-amber-400" : "bg-white/10"}`}></div>}
            </div>
        ))}
    </div>
);

const Field = ({ label, ...props }) => (
    <label className="block">
        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">{label}</div>
        <input
            {...props}
            className={`w-full bg-slate-900/60 border border-white/10 focus:border-amber-500/50 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-all ${props.className || ""}`}
        />
    </label>
);

// Grid responsivo por quantidade de planos
const GRID_COLS = {
    1: "grid-cols-1 max-w-sm mx-auto",
    2: "grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto",
    3: "grid-cols-1 md:grid-cols-3 max-w-4xl mx-auto",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto",
};

const PlanGrid = ({ plans, selectedSlug, onSelect }) => {
    const cols = GRID_COLS[plans.length] || "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto";
    const highlightIdx = Math.floor(plans.length / 2);
    return (
        <div data-testid="register-plans-grid" className={`grid gap-3 ${cols}`}>
            {plans.map((plan, idx) => (
                <PlanCard
                    key={plan.slug || plan.id}
                    plan={plan}
                    selected={selectedSlug === plan.slug}
                    highlighted={plans.length > 1 && idx === highlightIdx}
                    onSelect={() => onSelect(plan.slug)}
                    testId={`register-plan-${plan.slug}`}
                    variant="signup"
                    ctaLabel={selectedSlug === plan.slug ? "Selecionado" : `Escolher ${plan.name}`}
                />
            ))}
        </div>
    );
};

const PublicRegister = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [showPw, setShowPw] = useState(false);
    const [showPwConfirm, setShowPwConfirm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [termsModalOpen, setTermsModalOpen] = useState(false);

    // Captura UTMs da URL e do document.referrer no mount
    const utm = useMemo(() => {
        if (typeof window === "undefined") return {};
        const sp = new URLSearchParams(window.location.search);
        const readAndPersist = (key) => {
            const fromUrl = sp.get(key);
            if (fromUrl) {
                try { sessionStorage.setItem(`kinng.${key}`, fromUrl); } catch { /* ignore */ }
                return fromUrl;
            }
            try { return sessionStorage.getItem(`kinng.${key}`) || null; } catch { return null; }
        };
        const ref = (() => {
            try {
                const r = document.referrer || "";
                if (!r || r.includes(window.location.host)) return null;
                return r;
            } catch { return null; }
        })();
        // Ref de afiliado — cookie 30d + localStorage fallback
        const refCode = (() => {
            const fromUrl = sp.get("ref");
            if (fromUrl) {
                try {
                    document.cookie = `kinng_ref=${encodeURIComponent(fromUrl)}; max-age=${30 * 24 * 60 * 60}; path=/; SameSite=Lax`;
                    localStorage.setItem("kinng_ref", fromUrl);
                } catch { /* ignore */ }
                return fromUrl;
            }
            try {
                const cookie = document.cookie.split(";").find((c) => c.trim().startsWith("kinng_ref="));
                if (cookie) return decodeURIComponent(cookie.split("=")[1] || "");
                return localStorage.getItem("kinng_ref") || null;
            } catch { return null; }
        })();
        // Link de convite direto (Super Admin) — independente do afiliado.
        // Persistido na sessão só pra sobreviver à navegação entre passos.
        const invite = (() => {
            const fromUrl = sp.get("invite");
            if (fromUrl) {
                try { sessionStorage.setItem("kinng.invite", fromUrl); } catch { /* ignore */ }
                return fromUrl;
            }
            try { return sessionStorage.getItem("kinng.invite") || null; } catch { return null; }
        })();
        return {
            utm_source:   readAndPersist("utm_source"),
            utm_medium:   readAndPersist("utm_medium"),
            utm_campaign: readAndPersist("utm_campaign"),
            utm_content:  readAndPersist("utm_content"),
            utm_term:     readAndPersist("utm_term"),
            referrer:     ref,
            ref_code:     refCode,
            invite:       invite,
        };
    }, []);

    const [form, setForm] = useState({
        shop_name: "", business_type: "barbearia", phone: "",
        city: "", state: "",
        owner_name: "", email: "", password: "", password_confirm: "",
        document: "", document_type: "cpf",
        plan: "imperial", terms_accepted: false,
    });
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    // Planos dinâmicos vindos do backend
    const [plans, setPlans] = useState(PLANS_FALLBACK);
    useEffect(() => {
        plansPublicApi.list()
            .then((list) => {
                const actives = (list || []).filter((p) => p.active !== false);
                if (actives.length > 0) {
                    setPlans(actives);
                    // Ajusta plano default para um slug real caso "imperial" não exista
                    setForm((f) => (actives.some((p) => p.slug === f.plan) ? f : { ...f, plan: actives[Math.floor(actives.length / 2)].slug }));
                }
            })
            .catch(() => { /* fallback silencioso */ });
    }, []);

    const canNext = useMemo(() => {
        if (step === 1) {
            return form.shop_name.trim().length >= 2 && form.phone.replace(/\D/g,"").length >= 10
                && form.city.trim().length >= 2 && form.state.trim().length >= 2;
        }
        if (step === 2) {
            const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
            return form.owner_name.trim().length >= 2 && emailOk
                && form.password.length >= 8 && form.password === form.password_confirm
                && form.document.replace(/\D/g,"").length >= 11 && form.terms_accepted;
        }
        return true;
    }, [step, form]);

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                shop_name: form.shop_name.trim(),
                business_type: form.business_type,
                phone: form.phone,
                city: form.city.trim(),
                state: form.state.trim().toUpperCase().slice(0,2),
                owner_name: form.owner_name.trim(),
                email: form.email.trim().toLowerCase(),
                password: form.password,
                document: form.document,
                document_type: detectDocType(form.document),
                plan: form.plan,
                terms_accepted: form.terms_accepted,
                ...utm,
            };
            const res = await fetch(`${API_BASE}/public/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.detail || `Erro ${res.status}`);
            auth.setToken(data.token);
            auth.cacheMe({ kind: "tenant_user", user: data.user, tenant: data.tenant });
            // Limpa o cookie de afiliado após signup bem-sucedido
            try {
                document.cookie = "kinng_ref=; max-age=0; path=/";
                localStorage.removeItem("kinng_ref");
                sessionStorage.removeItem("kinng.invite");
            } catch { /* ignore */ }
            toast.success(`Bem-vindo ao Kinng.app, ${data.user.name.split(" ")[0]}!`, {
                description: data.message,
            });
            // Pequeno delay para o toast aparecer e o token ser aplicado
            setTimeout(() => { window.location.href = "/"; }, 900);
        } catch (e) {
            toast.error("Não foi possível criar sua conta", { description: e.message });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 pb-20" data-testid="public-register-page">
            <header className="border-b border-white/5">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/brand/kinng-mark-transparent.png" alt="Kinng.app" className="h-9 w-9" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Kinng.app</div>
                            <div className="salon-heading text-lg text-white leading-tight">Criar minha conta</div>
                        </div>
                    </div>
                    <Link to="/" className="text-xs text-slate-400 hover:text-amber-200 transition-all" data-testid="register-back-login">
                        Já tenho conta<i className="fa-solid fa-arrow-right ml-1"></i>
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10">
                <Stepper current={step} />

                {step === 1 && (
                    <section data-testid="register-step-1" className="space-y-5">
                        <div className="text-center mb-6">
                            <h1 className="salon-heading text-3xl sm:text-4xl text-white mb-2">Vamos configurar o seu salão!</h1>
                            <p className="text-sm text-slate-500">Comece contando pra gente sobre o seu negócio.</p>
                        </div>

                        <Field label="Nome do salão" placeholder="Ex: Vintage Club Barbearia" value={form.shop_name}
                            onChange={(e) => set({ shop_name: e.target.value })} data-testid="register-input-shop-name" />

                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2">Tipo de negócio</div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {BUSINESS_TYPES.map((t) => {
                                    const active = form.business_type === t.id;
                                    return (
                                        <button
                                            key={t.id}
                                            data-testid={`register-biz-${t.id}`}
                                            type="button"
                                            onClick={() => set({ business_type: t.id })}
                                            className={`rounded-xl border p-3 text-center text-xs transition-all ${
                                                active ? "border-amber-400/60 bg-amber-500/10 text-amber-100"
                                                       : "border-white/10 hover:border-amber-500/30 text-slate-400"
                                            }`}
                                        >
                                            <i className={`fa-solid ${t.icon} text-lg block mb-1`}></i>
                                            {t.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <Field label="Telefone do salão" placeholder="(11) 91234-5678" value={form.phone}
                            onChange={(e) => set({ phone: maskPhone(e.target.value) })}
                            data-testid="register-input-phone" className="font-mono" />

                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2">
                                <Field label="Cidade" placeholder="São Paulo" value={form.city}
                                    onChange={(e) => set({ city: e.target.value })} data-testid="register-input-city" />
                            </div>
                            <Field label="UF" placeholder="SP" value={form.state} maxLength={2}
                                onChange={(e) => set({ state: e.target.value.toUpperCase().replace(/[^A-Z]/g,"") })}
                                data-testid="register-input-state" className="uppercase text-center font-mono" />
                        </div>
                    </section>
                )}

                {step === 2 && (
                    <section data-testid="register-step-2" className="space-y-5">
                        <div className="text-center mb-6">
                            <h1 className="salon-heading text-3xl sm:text-4xl text-white mb-2">Agora crie seu acesso!</h1>
                            <p className="text-sm text-slate-500">Você será o proprietário e único administrador desta conta.</p>
                        </div>

                        <Field label="Nome completo" placeholder="Como devemos te chamar?" value={form.owner_name}
                            onChange={(e) => set({ owner_name: e.target.value })} data-testid="register-input-owner" />

                        <Field label="E-mail" type="email" placeholder="voce@email.com" value={form.email}
                            onChange={(e) => set({ email: e.target.value })} data-testid="register-input-email" />

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Senha</div>
                                <div className="relative">
                                    <input
                                        data-testid="register-input-password"
                                        type={showPw ? "text" : "password"}
                                        value={form.password}
                                        onChange={(e) => set({ password: e.target.value })}
                                        placeholder="mín. 8 caracteres"
                                        className="w-full bg-slate-900/60 border border-white/10 focus:border-amber-500/50 rounded-xl pl-4 pr-11 py-3 text-sm text-white placeholder:text-slate-600 outline-none"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPw((v) => !v)}
                                        aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
                                        className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-slate-500 hover:text-amber-200"
                                        data-testid="register-toggle-password"
                                    >
                                        <i className={`fa-solid ${showPw ? "fa-eye-slash" : "fa-eye"} text-xs`}></i>
                                    </button>
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Confirmar senha</div>
                                <div className="relative">
                                    <input
                                        data-testid="register-input-password-confirm"
                                        type={showPwConfirm ? "text" : "password"}
                                        value={form.password_confirm}
                                        onChange={(e) => set({ password_confirm: e.target.value })}
                                        placeholder="repita a senha"
                                        className="w-full bg-slate-900/60 border border-white/10 focus:border-amber-500/50 rounded-xl pl-4 pr-11 py-3 text-sm text-white placeholder:text-slate-600 outline-none"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPwConfirm((v) => !v)}
                                        aria-label={showPwConfirm ? "Ocultar senha" : "Mostrar senha"}
                                        className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-slate-500 hover:text-amber-200"
                                        data-testid="register-toggle-password-confirm"
                                    >
                                        <i className={`fa-solid ${showPwConfirm ? "fa-eye-slash" : "fa-eye"} text-xs`}></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        {form.password && form.password_confirm && form.password !== form.password_confirm && (
                            <div className="text-xs text-red-400">As senhas não conferem.</div>
                        )}

                        <Field label="CPF ou CNPJ" placeholder="000.000.000-00" value={form.document}
                            onChange={(e) => set({ document: maskDoc(e.target.value) })}
                            data-testid="register-input-document" className="font-mono" />

                        <label className="flex items-start gap-3 cursor-pointer group" data-testid="register-terms">
                            <input
                                type="checkbox"
                                checked={form.terms_accepted}
                                onChange={(e) => set({ terms_accepted: e.target.checked })}
                                className="mt-1 h-4 w-4 accent-amber-500 shrink-0"
                                data-testid="register-terms-checkbox"
                            />
                            <span className="text-xs text-slate-400 group-hover:text-slate-300">
                                Li e aceito os{" "}
                                <button
                                    type="button"
                                    data-testid="register-open-terms-modal"
                                    onClick={(e) => { e.preventDefault(); setTermsModalOpen(true); }}
                                    className="text-amber-300 underline"
                                >
                                    Termos de Uso
                                </button>{" "}
                                e a <a href="/privacidade" target="_blank" rel="noreferrer" className="text-amber-300 underline">Política de Privacidade</a> do Kinng.app.
                            </span>
                        </label>
                    </section>
                )}

                {step === 3 && (
                    <section data-testid="register-step-3" className="space-y-5">
                        <div className="text-center mb-6">
                            <h1 className="salon-heading text-3xl sm:text-4xl text-white mb-2">Escolha como quer começar:</h1>
                            <p className="text-sm text-slate-500">
                                <span className="text-emerald-300">Trial de 14 dias grátis</span> em todos os planos pagos. Sem cartão de crédito.
                            </p>
                        </div>
                        <PlanGrid
                            plans={plans}
                            selectedSlug={form.plan}
                            onSelect={(slug) => set({ plan: slug })}
                        />
                    </section>
                )}
            </main>

            <PublicFooter />

            <footer className="fixed bottom-0 inset-x-0 border-t border-white/5 bg-slate-950/95 backdrop-blur-xl">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
                    <button
                        data-testid="register-back-btn"
                        type="button"
                        onClick={() => setStep((s) => Math.max(1, s - 1))}
                        disabled={step === 1}
                        className="rounded-xl border border-white/10 text-slate-300 hover:text-white hover:border-white/20 px-4 py-2.5 text-sm disabled:opacity-30 transition-all"
                    >
                        <i className="fa-solid fa-arrow-left mr-2"></i>Voltar
                    </button>
                    <div className="text-xs text-slate-500 hidden sm:block">Passo {step} de 3</div>
                    {step < 3 ? (
                        <button
                            data-testid="register-next-btn"
                            type="button"
                            onClick={() => setStep((s) => s + 1)}
                            disabled={!canNext}
                            className="rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-6 py-2.5 text-sm disabled:opacity-40 transition-all"
                        >
                            Continuar<i className="fa-solid fa-arrow-right ml-2"></i>
                        </button>
                    ) : (
                        <button
                            data-testid="register-submit-btn"
                            type="button"
                            onClick={submit}
                            disabled={submitting || !canNext}
                            className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 py-2.5 text-sm disabled:opacity-40 transition-all"
                        >
                            {submitting ? (
                                <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Criando…</>
                            ) : (
                                <>Criar minha conta grátis<i className="fa-solid fa-arrow-right ml-2"></i></>
                            )}
                        </button>
                    )}
                </div>
            </footer>

            <TermsViewModal open={termsModalOpen} onClose={() => setTermsModalOpen(false)} />
        </div>
    );
};

export default PublicRegister;
